import { ensureFreshToken } from '@/lib/session';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export type DeclareEventInput = {
  eventId: string;
  occurredAt: string;
  attributeType: string;
  phoneNumber: string;
  source: { system: string; actorId?: string; actorName?: string };
  payload: Record<string, unknown>;
};

/**
 * POST /v1/events — declare a single event. We bypass the typed
 * openapi-fetch client here because the backend declares the body as
 * `RawBody []byte` (to dodge a Huma schema-name collision), which makes
 * the generated TS type `string` rather than the structured object. A
 * plain `fetch` with manual JSON.stringify is the cleanest workaround —
 * we still reuse the JWT from the auth store for the Bearer header.
 */
export type DeclareResult =
  | { ok: true; eventId: string; alreadyExisted: boolean }
  | { ok: false; code: string; message: string };

export async function declareEvent(input: DeclareEventInput): Promise<DeclareResult> {
  const token = await ensureFreshToken();
  const body = {
    event: {
      eventId: input.eventId,
      occurredAt: input.occurredAt,
      attributeType: input.attributeType,
      subject: { subjectType: 'ENTREPRENEUR', phoneNumber: input.phoneNumber },
      source: input.source,
      payload: input.payload,
    },
  };
  const response = await fetch(`${BASE_URL}/v1/events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      title?: string;
      detail?: string;
    };
    return {
      ok: false,
      code: errorBody.title ?? String(response.status),
      message: errorBody.detail ?? 'Erreur inconnue',
    };
  }
  const data = (await response.json()) as {
    eventId?: string;
    alreadyExisted?: boolean;
  };
  return {
    ok: true,
    eventId: data.eventId ?? input.eventId,
    alreadyExisted: data.alreadyExisted ?? false,
  };
}

/**
 * UUIDv4 generator that runs in the browser. We mint the eventId
 * client-side so the request is idempotent on retry — the backend
 * dedupes on eventId.
 */
export function newEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: RFC 4122 v4 from Math.random — never reached on modern
  // browsers, kept for SSR/tests sanity.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
